/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Observable, Subject, finalize, interval, map, of, takeWhile } from 'rxjs';

export interface PreloaderService {
    loading: () => boolean;
    error: () => boolean;
    data: () => any;
    reset: () => void;
}

interface WaitUntilConfig {
    timeout: number;
    condition: () => boolean;
}

type PreloaderFunction = (target: object, propertyKey: string, descriptor: TypedPropertyDescriptor<any>) => TypedPropertyDescriptor<any>;

const WaitTimeout = 10_000;

/**
 *
 *    When the page loads, some data is loaded before the angular up is started.
 *  This checks if the data is present and if so it does not call the backend again but resolves with the preloaded data.
 *
 * @export
 * @param config
 */
// eslint-disable-next-line max-lines-per-function
export function usePreloaderData(config: {
    preloader: () => PreloaderService;
    responseMapper?: (data: any) => any;
    condition?: (param1: any) => boolean;
}): PreloaderFunction {
    return function (_: object, __: string, descriptor: TypedPropertyDescriptor<any>): TypedPropertyDescriptor<any> {
        descriptor.value = getDecoratedFunction(descriptor.value);

        return descriptor;
    };

    function getDecoratedFunction(originalFunction: () => void): any {
        return function (this: any, ...args: any[]): any {
            const preloader = config.preloader.bind(this)();
            // eslint-disable-next-line unicorn/no-this-assignment, @typescript-eslint/no-this-alias
            const self = this;

            if (config.condition && !config.condition.bind(this)(args[0])) {
                return originalFunction.apply(self, args as any);
            }

            config.responseMapper = config.responseMapper || ((data) => data);

            if (preloader && preloader.error()) {
                preloader.reset();

                return originalFunction.apply(self, args as any);
            }

            if (preloader && preloader.loading()) {
                return waitUntil$({
                    condition: () => !preloader.loading(),
                    timeout: WaitTimeout,
                }).pipe(
                    map((status) => {
                        if (status === 'success' && !preloader.error()) {
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                            config = config || <any>{};
                            config.responseMapper =
                                config.responseMapper ||
                                function (input: any): any {
                                    return input;
                                };

                            return config.responseMapper(preloader.data());
                        }

                        return originalFunction.apply(self, args as any);
                    }),
                    finalize(() => preloader.reset()),
                );
            }

            if (preloader && preloader.data()) {
                const data = preloader.data();
                preloader.reset();

                return of(config.responseMapper(data));
            }

            return originalFunction.apply(self, args as any);
        };
    }

    // eslint-disable-next-line unicorn/consistent-function-scoping
    function waitUntil$(options: WaitUntilConfig): Observable<'timeout' | 'success'> {
        let totalWaited = 0;
        const waiting$ = new Subject<'timeout' | 'success'>();

        interval(100)
            .pipe(
                takeWhile(() => {
                    totalWaited += 100;

                    if (options.condition()) {
                        waiting$.next('success');
                        waiting$.complete();

                        return false;
                    }

                    if (totalWaited > options.timeout) {
                        if (options.condition()) {
                            waiting$.next('success');
                            waiting$.complete();

                            return false;
                        }

                        waiting$.next('timeout');
                        waiting$.complete();

                        return false;
                    }

                    return true;
                }),
            )
            .subscribe();

        return waiting$.asObservable();
    }
}
